import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../config/apiService';

// SIP sites — see SupervisorQualManager.js for the same constant + rationale.
const SIP_SITES = [
    { siteID: 5, site: 'CW Valencia 1', code: 'VAL01' },
    { siteID: 6, site: 'CW Valencia 2', code: 'VAL02' },
    { siteID: 3, site: 'CW Sacramento', code: 'SAC05' },
];

// key = PascalCase column / save-payload field; jsonKey = camelCase property
// in the GET response. Written explicitly so abbreviations like DWC1 (in the
// supervisor variant) don't break silently.
const CRITERIA = [
    { key: 'NoSafetyWarning',          jsonKey: 'noSafetyWarning',          short: 'No Warning', long: 'No oral/written safety warning issued.' },
    { key: 'CompletedSafetyTraining',  jsonKey: 'completedSafetyTraining',  short: 'Training',   long: 'Completed all safety training.' },
    { key: 'AttendedToolboxMeetings',  jsonKey: 'attendedToolboxMeetings',  short: 'Toolbox',    long: 'Attended all toolbox meetings.' },
    { key: 'ReportedAccidents',        jsonKey: 'reportedAccidents',        short: 'Accidents',  long: 'Reported all accidents / injuries.' },
    { key: 'NotOnFinalWrittenWarning', jsonKey: 'notOnFinalWrittenWarning', short: 'Not Final',  long: 'Not on a final written warning.' },
];

function EmployeeQualManager() {
    const now = new Date();
    const access = (localStorage.getItem('admin_access_visibility') || 'ALL-ALL').toUpperCase();
    const myCode = access.split('-')[1] || 'ALL';
    const visibleSites = myCode === 'ALL' ? SIP_SITES : SIP_SITES.filter((s) => s.code === myCode);

    const [siteID, setSiteID] = useState(visibleSites[0]?.siteID || SIP_SITES[0].siteID);
    // Default to LAST month — the reporting period being qualified this month.
    const _lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [yyyymm, setYyyymm] = useState(`${_lm.getFullYear()}-${String(_lm.getMonth() + 1).padStart(2, '0')}`);
    const [year, month] = useMemo(() => {
        const [y, m] = yyyymm.split('-').map(Number);
        return [y, m];
    }, [yyyymm]);

    const [rows, setRows] = useState([]);
    const [edits, setEdits] = useState({});
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');

    const flash = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };

    const reload = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getEmployeeQualifications(siteID, year, month);
            const list = Array.isArray(res.data) ? res.data : [];
            setRows(list);
            const seed = {};
            for (const r of list) {
                seed[r.employeeID] = {};
                for (const c of CRITERIA) seed[r.employeeID][c.key] = !!r[c.jsonKey];
            }
            setEdits(seed);
            setMsg('');
        } catch (err) {
            flash(`Load failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
            setRows([]); setEdits({});
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [siteID, yyyymm]);

    // Group by department (DepartmentDisplayOrder, then Department name).
    const groups = useMemo(() => {
        const map = new Map();
        for (const r of rows) {
            const key = `${r.departmentDisplayOrder}|${r.departmentID}|${r.department || '—'}`;
            if (!map.has(key)) map.set(key, {
                departmentDisplayOrder: r.departmentDisplayOrder,
                departmentID: r.departmentID,
                department: r.department || '(no department)',
                rows: []
            });
            map.get(key).rows.push(r);
        }
        return [...map.values()].sort((a, b) =>
            (a.departmentDisplayOrder - b.departmentDisplayOrder) || a.department.localeCompare(b.department)
        );
    }, [rows]);

    const handleEdit = (employeeID, criterionKey, checked) => {
        setEdits((prev) => ({
            ...prev,
            [employeeID]: { ...(prev[employeeID] || {}), [criterionKey]: checked }
        }));
    };

    // Reportable for this site/month? noReportableForMonth === false = a
    // recordable happened (Monthly Points "Yes"). Flips employees not-eligible.
    const reportableYes = rows.some((r) => r.noReportableForMonth === false);

    const isLocallyEligible = (employeeID) => {
        if (reportableYes) return false;
        const e = edits[employeeID];
        if (!e) return false;
        return CRITERIA.every((c) => e[c.key] === true);
    };

    const handleSave = async () => {
        const payload = rows.map((r) => ({
            EmployeeID: r.employeeID,
            NoSafetyWarning:          !!edits[r.employeeID]?.NoSafetyWarning,
            CompletedSafetyTraining:  !!edits[r.employeeID]?.CompletedSafetyTraining,
            AttendedToolboxMeetings:  !!edits[r.employeeID]?.AttendedToolboxMeetings,
            ReportedAccidents:        !!edits[r.employeeID]?.ReportedAccidents,
            NotOnFinalWrittenWarning: !!edits[r.employeeID]?.NotOnFinalWrittenWarning,
        }));
        setBusy(true); setMsg('');
        try {
            await adminAPI.saveEmployeeQualifications({ siteID, year, month, rows: payload });
            await reload();
            flash('Saved.', 'success');
        } catch (err) {
            flash(`Save failed: ${err?.response?.data || err?.message || 'unknown error'}`, 'error');
        } finally { setBusy(false); }
    };

    // Per-column check-all (scoped to a department's rows).
    const setColumnForGroup = (groupRows, criterionKey, checked) => {
        setEdits((prev) => {
            const next = { ...prev };
            for (const r of groupRows) {
                next[r.employeeID] = { ...(next[r.employeeID] || {}), [criterionKey]: checked };
            }
            return next;
        });
    };
    const columnState = (groupRows, criterionKey) => {
        const vals = groupRows.map((r) => !!edits[r.employeeID]?.[criterionKey]);
        if (vals.length && vals.every((v) => v)) return 'all';
        if (vals.some((v) => v)) return 'some';
        return 'none';
    };

    // Sheet-wide controls (every employee, across all department groups).
    const setColumnAll = (criterionKey, checked) => setColumnForGroup(rows, criterionKey, checked);
    const columnStateAll = (criterionKey) => columnState(rows, criterionKey);
    const setEverything = (checked) => {
        setEdits(() => {
            const next = {};
            for (const r of rows) {
                next[r.employeeID] = {};
                for (const c of CRITERIA) next[r.employeeID][c.key] = checked;
            }
            return next;
        });
    };
    const everythingState = () => {
        const vals = [];
        for (const r of rows) for (const c of CRITERIA) vals.push(!!edits[r.employeeID]?.[c.key]);
        if (vals.length && vals.every((v) => v)) return 'all';
        if (vals.some((v) => v)) return 'some';
        return 'none';
    };

    // Removing an employee lives on the Load Employees page (click a name in
    // the roster). No remove control here.

    const monthLabel = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
    const eligibleCount = rows.filter((r) => isLocallyEligible(r.employeeID)).length;

    return (
        <div>
            <div style={styles.card}>
                <div style={styles.eyebrow}>Employee Qualification Maintenance</div>
                <div style={styles.title}>{monthLabel}</div>
                <div style={styles.muted}>
                    Toggle each criterion per employee. Eligibility is computed from all 5 being Yes —
                    those rows feed the Drawing pool for this site/month.
                </div>
                <div style={styles.bulkRow}>
                    <label style={styles.bulkLabel}>Site:</label>
                    <select value={siteID} onChange={(e) => setSiteID(Number(e.target.value))} style={styles.input} disabled={visibleSites.length === 1}>
                        {visibleSites.map((s) => <option key={s.siteID} value={s.siteID}>{s.site}</option>)}
                    </select>
                    <label style={styles.bulkLabel}>Month:</label>
                    <input type="month" value={yyyymm} onChange={(e) => setYyyymm(e.target.value)} style={styles.input} />
                    <span style={{ ...styles.muted, marginLeft: 'auto' }}>
                        {rows.length} employee{rows.length === 1 ? '' : 's'} · {eligibleCount} eligible
                    </span>
                    <button onClick={handleSave} disabled={busy || loading || rows.length === 0} style={btn('#2563eb', busy || loading || rows.length === 0)}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>

            {reportableYes && (
                <div style={styles.reportableNote}>
                    ⚠ Recordable During Period — a recordable incident occurred at this location group this month.
                    Every employee here is not eligible for the drawing regardless of the criteria below.
                </div>
            )}

            {msg && <Banner kind={msgKind}>{msg}</Banner>}

            {!loading && groups.length > 0 && (
                <div style={{ ...styles.card, marginTop: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ ...styles.bulkLabel, display: 'flex', alignItems: 'center', gap: 6, color: '#111827' }}>
                        <input
                            type="checkbox"
                            checked={everythingState() === 'all'}
                            ref={(el) => { if (el) el.indeterminate = everythingState() === 'some'; }}
                            onChange={(e) => setEverything(e.target.checked)}
                        />
                        Check ALL — whole sheet
                    </label>
                    <span style={styles.muted}>or one criterion across every department:</span>
                    {CRITERIA.map((c) => {
                        const st = columnStateAll(c.key);
                        return (
                            <label key={c.key} style={{ ...styles.bulkLabel, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }} title={c.long}>
                                <input
                                    type="checkbox"
                                    checked={st === 'all'}
                                    ref={(el) => { if (el) el.indeterminate = st === 'some'; }}
                                    onChange={(e) => setColumnAll(c.key, e.target.checked)}
                                />
                                {c.short}
                            </label>
                        );
                    })}
                </div>
            )}

            {loading ? (
                <div style={{ ...styles.card, marginTop: 12 }}><div style={styles.muted}>Loading…</div></div>
            ) : groups.length === 0 ? (
                <div style={{ ...styles.card, marginTop: 12 }}><div style={styles.muted}>No employees found at this site.</div></div>
            ) : (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {groups.map((g) => (
                        <div key={g.departmentID} style={styles.card}>
                            <div style={styles.groupHeader}>{g.department}</div>
                            <table style={styles.grid}>
                                <thead style={styles.gridHead}>
                                    <tr>
                                        <th style={styles.th}>Employee</th>
                                        {CRITERIA.map((c) => (
                                            <th key={c.key} style={{ ...styles.th, textAlign: 'center', width: 90 }} title={c.long}>{c.short}</th>
                                        ))}
                                        <th style={{ ...styles.th, textAlign: 'center', width: 100 }}>Eligible</th>
                                    </tr>
                                    <tr>
                                        <th style={{ ...styles.th, fontStyle: 'italic', fontWeight: 600, color: '#6b7280' }}>check all →</th>
                                        {CRITERIA.map((c) => {
                                            const st = columnState(g.rows, c.key);
                                            return (
                                                <th key={c.key} style={{ ...styles.th, textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={st === 'all'}
                                                        ref={(el) => { if (el) el.indeterminate = st === 'some'; }}
                                                        onChange={(e) => setColumnForGroup(g.rows, c.key, e.target.checked)}
                                                        title={`Check/uncheck "${c.short}" for all in ${g.department}`}
                                                    />
                                                </th>
                                            );
                                        })}
                                        <th style={styles.th}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {g.rows.map((r) => {
                                        const elig = isLocallyEligible(r.employeeID);
                                        return (
                                            <tr key={r.employeeID}>
                                                <td style={styles.td}>
                                                    <span>{r.lastName}, {r.firstName} <span style={{ color: '#9ca3af', fontSize: 11 }}>#{r.employeeID}</span></span>
                                                </td>
                                                {CRITERIA.map((c) => (
                                                    <td key={c.key} style={{ ...styles.td, textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!edits[r.employeeID]?.[c.key]}
                                                            onChange={(e) => handleEdit(r.employeeID, c.key, e.target.checked)}
                                                        />
                                                    </td>
                                                ))}
                                                <td style={{ ...styles.td, textAlign: 'center' }}>
                                                    <EligibilityBadge ok={elig} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function EligibilityBadge({ ok }) {
    const style = ok
        ? { background: '#dcfce7', color: '#15803d', borderColor: '#bbf7d0' }
        : { background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' };
    return <span style={{ ...styles.badge, ...style }}>{ok ? 'ELIGIBLE' : 'NOT ELIGIBLE'}</span>;
}

function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 4,
    fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    title: { fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 2 },
    muted: { color: '#6b7280', fontSize: 13 },
    reportableNote: { margin: '12px 0', padding: '10px 14px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13, fontWeight: 700 },
    bulkRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 },
    bulkLabel: { fontSize: 12, fontWeight: 700, color: '#374151' },
    input: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
    groupHeader: { fontSize: 13, fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 },
    grid: { width: '100%', borderCollapse: 'collapse' },
    gridHead: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    th: { textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '6px 10px', fontSize: 13, color: '#111827', borderBottom: '1px solid #f3f4f6' },
    badge: { display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', border: '1px solid', borderRadius: 4 }
};

export default EmployeeQualManager;
